import { memo, useMemo } from 'react'

import { poolSplit } from '../constants'
import type { AllocationBoard } from '../types'

/**
 * Usage ribbon: a horizontal stacked bar of the whole phone-line pool, one
 * segment per duty (width = reserved / pool.total) plus a leftover "free"
 * segment.
 *
 * Every reserved line is attributed to a duty, so `Σ reserved_by_duty` equals
 * `pool.reserved` and the leftover — `(total − Σ reserved_by_duty) / total` —
 * lands on `pool.free`. It's computed from the segments (not read from
 * `pool.free`) so the bar always sums to exactly 100%; any reserved line with no
 * duty is folded into the leftover.
 *
 * Legend below: swatch · duty name · priority N (looked up from the backlog
 * `duties` by duty_id, using the UI label "priority" for the contract's
 * `importance` field). Only shown when priority > 1 — the default weight is noise;
 * a duty holding lines but absent from `duties[]` — the archived-duty case, since
 * inclusion there is a union that already covers `reserved > 0` — has no importance
 * to look up and omits the label entirely.
 */
export const AllocationRibbon = memo(function AllocationRibbon({
  board,
  colorFor,
}: {
  board: AllocationBoard
  colorFor: (dutyId: string) => string
}) {
  // Destructured so the memo below can depend on exactly the fields it reads.
  // Deliberately not keyed on `board`: its identity changes every poll because of
  // `generated_at`, which would defeat the memo. These three are ref-stable across
  // unchanged polls via React Query's structural sharing.
  const { pool, reserved_by_duty: reservedByDuty, duties } = board

  // Derived once per data change rather than per render, mirroring how the sibling
  // table memoizes its sort.
  const { total, sumReserved, freeLines, segments, importanceById } =
    useMemo(() => {
      // poolSplit is shared with the page header so the two can't disagree. `free` is
      // floored at zero: under backend drift (Σ reserved > total) a negative width is
      // invalid CSS — the declaration is dropped, the segment sizes to its content and
      // overflows the bar. The aria-label below still reports the raw `held`, so the
      // drift stays visible rather than being absorbed by the clamp.
      const split = poolSplit({ pool, reserved_by_duty: reservedByDuty })
      return {
        total: split.total,
        sumReserved: split.held,
        freeLines: split.free,
        // Stable order (by duty_id) so a duty keeps its position as ranks change
        // between polls — it grows/shrinks in place instead of sliding left/right when
        // it becomes the largest. Ranking still lives in the table.
        segments: [...reservedByDuty].toSorted((a, b) =>
          a.duty_id.localeCompare(b.duty_id)
        ),
        importanceById: new Map(duties.map((d) => [d.duty_id, d.importance])),
      }
    }, [pool, reservedByDuty, duties])

  const freePct = total > 0 ? (freeLines / total) * 100 : 0
  const pct = (reserved: number) => (total > 0 ? (reserved / total) * 100 : 0)

  // role="img" prunes the subtree from the accessibility tree, so the in-segment
  // labels and the per-segment `title` counts never reach a screen reader. Without
  // the breakdown here, the whole region announced as a single total — sighted users
  // got a per-duty split and screen-reader users got one number. Spell the split out
  // in the accessible name instead.
  const segmentSummary = segments
    .map((d) => `${d.duty_name ?? 'Unnamed'} ${d.reserved}`)
    .join(', ')
  const ribbonLabel = `Line pool: ${sumReserved} of ${total} lines held across ${segments.length} ${
    segments.length === 1 ? 'duty' : 'duties'
  }${segmentSummary ? ` — ${segmentSummary}` : ''}; ${freeLines} free`

  return (
    <div>
      <div
        role="img"
        aria-label={ribbonLabel}
        className="flex h-11 overflow-hidden rounded-lg border border-border">
        {segments.map((duty) => (
          <div
            key={duty.duty_id}
            data-duty-id={duty.duty_id}
            title={`${duty.duty_name ?? 'Unnamed duty'} · ${duty.reserved} lines`}
            // Color is the duty's stable hue; width eases when its share of the
            // pool shifts between polls (motion-safe honors prefers-reduced-motion).
            style={{
              width: `${pct(duty.reserved)}%`,
              backgroundColor: colorFor(duty.duty_id),
            }}
            className="flex items-center justify-center overflow-hidden whitespace-nowrap px-1 text-[11px] font-semibold text-white motion-safe:transition-[width] motion-safe:duration-fast motion-safe:ease-smooth-out">
            <span className="truncate">
              {duty.duty_name ?? 'Unnamed'} {duty.reserved}
            </span>
          </div>
        ))}
        <div
          data-free
          title={`${freeLines} free of ${total}`}
          style={{ width: `${freePct}%` }}
          className="flex items-center justify-center overflow-hidden whitespace-nowrap bg-muted px-1 text-[11px] font-semibold text-muted-foreground motion-safe:transition-[width] motion-safe:duration-fast motion-safe:ease-smooth-out">
          <span className="truncate">{freeLines} free</span>
        </div>
      </div>

      {segments.length > 0 && (
        <div className="mt-3.5 flex flex-wrap gap-x-4 gap-y-2.5">
          {segments.map((duty) => {
            const importance = importanceById.get(duty.duty_id)
            return (
              <div
                key={duty.duty_id}
                className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className="inline-block size-2.5 rounded-[3px]"
                  style={{ backgroundColor: colorFor(duty.duty_id) }}
                />
                <span className="font-medium text-foreground">
                  {duty.duty_name ?? 'Unnamed'}
                </span>
                {importance !== undefined && importance > 1 && (
                  <span>· priority {importance}</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
})
